/**
 * ManageCms object represents CMS-related API endpoints and their methods.
 */
const ManageCms = {
  /**
   * Endpoint for retrieving contact information.
   * @memberof ManageCms
   * @type {object}
   * @property {string} url - The URL for retrieving contact information.
   * @property {string} method - The HTTP method for retrieving contact information (GET).
   */

  contactUs: {
    url: "/cms/site-setting",
    method: "GET",
  },

  /**
   * Endpoint for updating contact information.
   * @memberof ManageCms
   * @type {object}
   * @property {string} url - The URL for updating contact information.
   * @property {string} method - The HTTP method for updating contact information (POST).
   */

  updateContactUs: {
    url: "/cms/site-setting",
    method: "POST",
  },

  /**
   * Endpoint for retrieving terms and privacy content by ID.
   * @memberof ManageCms
   * @function getCmsTermAndPrivacy
   * @param {string} id - The ID of the terms and privacy content.
   * @returns {object} The endpoint object containing URL and method.
   */
  getCmsTermAndPrivacy: (id) => ({
    url: `cms/static-content/${id}`,
    method: "GET",
  }),

  /**
   * Endpoint for updating terms and privacy content by ID.
   * @memberof ManageCms
   * @function updateCmsTermAndPrivacy
   * @param {string} id - The ID of the terms and privacy content.
   * @returns {object} The endpoint object containing URL and method.
   */

  updateCmsTermAndPrivacy: (id) => ({
    url: `cms/static-content/${id}`,
    method: "PUT",
  }),

  /**
   * Endpoint for adding a frequently asked question (FAQ).
   * @memberof ManageCms
   * @type {object}
   * @property {string} url - The URL for adding an FAQ.
   * @property {string} method - The HTTP method for adding an FAQ (POST).
   */

  addFaq: {
    url: "/faq",
    method: "POST",
  },

  /**
   * Endpoint for retrieving all FAQs.
   * @memberof ManageCms
   * @type {object}
   * @property {string} url - The URL for retrieving all FAQs.
   * @property {string} method - The HTTP method for retrieving all FAQs (GET).
   */
  
  getFaq: {
    url: "/faq",
    method: "GET",
  },

  /**
   * Endpoint for deleting a specific FAQ by ID.
   * @memberof ManageCms
   * @function deleteFaq
   * @param {string} id - The ID of the FAQ to be deleted.
   * @returns {object} The endpoint object containing URL and method.
   */
  deleteFaq: (id) => ({
    url: `/faq/${id}`,
    method: "DELETE",
  }),

  /**
   * Endpoint for retrieving a specific FAQ by ID.
   * @memberof ManageCms
   * @function getSingleFAQs
   * @param {string} id - The ID of the FAQ to retrieve.
   * @returns {object} The endpoint object containing URL and method.
   */
  getSingleFAQs: (id) => ({
    url: `/faq/${id}`,
    method: "GET",
  }),

  /**
   * Endpoint for updating a specific FAQ by ID.
   * @memberof ManageCms
   * @function updateFAQ
   * @param {string} id - The ID of the FAQ to update.
   * @returns {object} The endpoint object containing URL and method.
   */
  updateFAQ: (id) => ({
    url: `/faq/${id}`,
    method: "PUT",
  }),


    /**
   * Endpoint for retrieving CMS content by ID.
   * @memberof ManageCms
   * @function getSingleCms
   * @param {string} id - The ID of the CMS content.
   * @returns {object} The endpoint object containing URL and method.
   */
    getSingleCms: (id) => ({
      url: `/cms/${id}`,
      method: "GET",
    }),
  
    /**
     * Endpoint for updating CMS content by ID.
     * @memberof ManageCms
     * @function updateCms
     * @param {string} id - The ID of the CMS content.
     * @returns {object} The endpoint object containing URL and method.
     */
  
    updateCms: (id) => ({
      url: `/cms/${id}`,
      method: "PUT",
    }),
};

// Export the ManageCms object for use in other modules
export default ManageCms;
